/**
 * Component for rendering GoogleMaps UI
 */

import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { Branch } from 'src/components/GoogleMaps/GoogleMapsTypes';

import { BranchLocationQueryContext } from '../StoreLocator/BranchLocationQuery';

import { googleMapsMarkerInfo } from './GoogleMapsMarkerInfo';
import { useGoogleMapsApi } from './GoogleMapsApiProvider';
import { MapTypeId, MapOptions } from './googleMapsApi';
import './styles/Map.scss';

interface GoogleMapProps {
    visibleBranches: Branch[];
    mapOptions: MapOptions;
}

const defaultGoogleMapProps: Pick<GoogleMapProps, 'mapOptions'> = {
    mapOptions: {
        mapTypeControl: false,
        streetViewControl: false,
        initialCenter: 'earth',
        mapTypeId: MapTypeId.ROADMAP,
        zoom: 13,
    },
};

export function GoogleMap({ visibleBranches, mapOptions }: GoogleMapProps) {
    const { branches } = useContext(BranchLocationQueryContext);
    const branchesLoop = visibleBranches.length ? visibleBranches : branches;
    const mapRef = useRef<HTMLDivElement>(null);
    const { apiIsReady, apiIsLoading, loadGoogleMapsApi } = useGoogleMapsApi();

    const loadMap = useCallback(async () => {
        const markers: google.maps.Marker[] = [];
        const bounds = new google.maps.LatLngBounds();
        const useCenter = branchesLoop.length === 1;
        let options = Object.assign(defaultGoogleMapProps.mapOptions, mapOptions);
        let openInfoWindow;

        // If we only have 1 branch (branch view page)
        // don't use bounds but instead set center value instead
        if (useCenter) {
            const { latitude, longitude } = branchesLoop[0];
            const center = new google.maps.LatLng(latitude, longitude);
            options = { ...options, center };
        }

        // Create our new map
        const map = new google.maps.Map(mapRef.current as HTMLDivElement, {
            ...options,
        });

        // Loop through each branch and create a marker
        branchesLoop.forEach((branch) => {
            // Create new marker
            const marker = new google.maps.Marker({
                position: {
                    lat: branch.latitude,
                    lng: branch.longitude,
                },
                map,
                title: branch.name,
                animation: google.maps.Animation.DROP,
            });

            // Create new info window
            const infoWindow = new google.maps.InfoWindow({
                content: googleMapsMarkerInfo(branch),
            });

            // Extend the bounds to include each marker's position)
            if (!useCenter) {
                bounds.extend(marker.getPosition() as google.maps.LatLng);
            }

            // Add click event handler when clicking the marker
            marker.addListener('click', () => {
                // If we already have an infoWindow open, close it
                // only 1 infoWindow open at a time
                if (openInfoWindow) {
                    openInfoWindow.close();
                }

                openInfoWindow = infoWindow;
                infoWindow.open(map, marker);
            });

            markers.push(marker);
        });

        // Fit the map to the newly inclusive bounds (positions it center between all the markers
        if (!useCenter) {
            map.fitBounds(bounds);
        }
    }, [branchesLoop, mapOptions]);

    // Load google maps api and load map when ready
    useEffect(() => {
        if (apiIsReady) {
            loadMap();
        } else if (!apiIsLoading) {
            loadGoogleMapsApi();
        }
    }, [apiIsReady, apiIsLoading, loadMap, loadGoogleMapsApi]);

    return <div id="map" ref={mapRef} />;
}

GoogleMap.defaultProps = defaultGoogleMapProps;
